import { AlertCircle, Banknote, CheckCircle2, Coins, CreditCard, IndianRupee, TrendingUp, Users, Wallet } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { SummaryCard } from '@/components/SummaryCard';
import { useDashboardSummaryQuery } from '@/api/query/dashboard';
import { useAppContext } from '@/contexts/AppContext';
import type { DashboardSummary } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';
import { MetricRow, SectionCard, percentOf } from './dashboard/SectionCard';
import { DonutChart, GrowthChart, MonthlyCollectionChart } from './dashboard/Charts';
import { AttentionCard, RecentTransactionsCard } from './dashboard/Lists';

/** A member count, or '--' until the backend sends member_stats. */
const memberCount = (value?: number) => (value === undefined ? '--' : String(value));

/** Gender, age and status breakdowns from member_stats. */
const memberBreakdowns = (stats: DashboardSummary['memberStats']) => [
  {
    title: 'Members by Gender',
    description: 'All members by gender',
    rows: [
      ['Male', stats?.male],
      ['Female', stats?.female],
      ['Others', stats?.others],
    ],
  },
  {
    title: 'Members by Age',
    description: 'All members by age today',
    rows: [
      ['Under 5', stats?.under5],
      ['5 – 17', stats?.age5To17],
      ['18 – 59', stats?.age18To59],
      ['60 & Above', stats?.age60AndAbove],
    ],
  },
  {
    title: 'Member Status',
    description: 'Policies held and life status',
    rows: [
      ['With Policy', stats?.withPolicy],
      ['No Policy', stats?.withoutPolicy],
      ['Alive', stats?.alive],
      ['Deceased', stats?.deceased],
      ['Claims Settled', stats?.claimsSettled],
    ],
  },
] as const;

function KpiCards({ kpis, memberStats }: Pick<DashboardSummary, 'kpis' | 'memberStats'>) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-7">
      <SummaryCard
        icon={Users}
        tone="brand"
        label="Total Members"
        value={String(kpis.totalMembers)}
        rows={[
          ['Joined', memberCount(memberStats?.joinedThisMonth)],
          ['No Policy', memberCount(memberStats?.withoutPolicy)],
        ]}
      />
      <SummaryCard
        icon={CheckCircle2}
        tone="emerald"
        label="Paid This Month"
        value={String(kpis.paidThisMonth)}
        rows={[
          ['Policy', `${kpis.policyPaid} / ${kpis.policyLive}`],
          ['Loan', `${kpis.loanPaid} / ${kpis.loanPaid + kpis.loanDue}`],
        ]}
      />
      <SummaryCard
        icon={AlertCircle}
        tone="amber"
        label="Unpaid This Month"
        value={String(kpis.unpaidThisMonth)}
        rows={[
          ['Policy', String(kpis.policyLive - kpis.policyPaid)],
          ['Loan', String(kpis.loanDue)],
        ]}
      />
      <SummaryCard
        icon={IndianRupee}
        tone="teal"
        label="Collected This Month"
        value={formatCurrency(kpis.collectedThisMonth)}
        rows={[
          ['Policy', formatCurrency(kpis.policyCollectedThisMonth)],
          ['Loan', formatCurrency(kpis.loanCollectedThisMonth)],
        ]}
      />
      <SummaryCard
        icon={Wallet}
        tone="gray"
        label="Lifetime Collected"
        value={formatCurrency(kpis.lifetimeCollected)}
        rows={[
          ['This Month', formatCurrency(kpis.collectedThisMonth)],
          ['Paid by PMS', formatCurrency(kpis.paidByPmsThisMonth)],
        ]}
      />
      <SummaryCard
        icon={Banknote}
        tone="brand"
        label="Loan Outstanding"
        value={formatCurrency(kpis.loanOutstanding)}
        rows={[
          ['Active Loans', String(kpis.loansActive)],
          ['Given', formatCurrency(kpis.loansGiven)],
        ]}
      />
      <SummaryCard
        icon={Coins}
        tone="gray"
        label="Member Balance"
        value={formatCurrency(kpis.memberBalanceDue)}
        rows={[
          ['Members', String(kpis.membersWithBalance)],
          ['Interest', formatCurrency(kpis.balanceInterestDue)],
        ]}
      />
    </div>
  );
}

function FinancialHealth({ summary }: { summary: DashboardSummary }) {
  const { kpis, paymentMethods } = summary;
  const policyRate = percentOf(kpis.policyCollectedThisMonth, kpis.policyTargetThisMonth);
  const methodTotal = paymentMethods.cash + paymentMethods.online;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Policy Collection Rate</span>
          <span className="text-2xl font-bold tabular-nums text-[#4369a0]">{policyRate}%</span>
        </div>
        <Progress value={policyRate} className="h-2.5 bg-gray-200 [&>div]:bg-[#4369a0]" />
        <p className="mt-2 text-xs text-gray-500">
          {formatCurrency(kpis.policyCollectedThisMonth)} received of {formatCurrency(kpis.policyTargetThisMonth)} target
        </p>
      </div>

      <MetricRow
        label="Policy Pending This Month"
        value={formatCurrency(Math.max(0, kpis.policyTargetThisMonth - kpis.policyCollectedThisMonth))}
        progress={policyRate}
        tone="amber"
      />
      <MetricRow label="Loan Repaid This Month" value={formatCurrency(kpis.loanCollectedThisMonth)} tone="teal" />
      <MetricRow label="Loan Outstanding" value={formatCurrency(kpis.loanOutstanding)} />
      <MetricRow label="Member Balance Due" value={formatCurrency(kpis.memberBalanceDue)} />
      <MetricRow label="Paid by PMS This Month" value={formatCurrency(kpis.paidByPmsThisMonth)} />

      <div className="grid grid-cols-2 gap-3 pt-1">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
          <div className="mb-1 flex items-center gap-1.5 text-emerald-700">
            <Banknote className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Cash</span>
          </div>
          <p className="text-sm font-semibold tabular-nums text-gray-900">{formatCurrency(paymentMethods.cash)}</p>
          <p className="text-xs text-gray-500">{percentOf(paymentMethods.cash, methodTotal)}% share</p>
        </div>
        <div className="rounded-lg border border-[#4369a0]/15 bg-[#4369a0]/5 p-3">
          <div className="mb-1 flex items-center gap-1.5 text-[#4369a0]">
            <CreditCard className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Online</span>
          </div>
          <p className="text-sm font-semibold tabular-nums text-gray-900">{formatCurrency(paymentMethods.online)}</p>
          <p className="text-xs text-gray-500">{percentOf(paymentMethods.online, methodTotal)}% share</p>
        </div>
      </div>
    </div>
  );
}

function LoanPortfolio({ portfolio }: { portfolio: DashboardSummary['loanPortfolio'] }) {
  const repaidRate = percentOf(portfolio.principalRepaid, portfolio.given);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Principal Repaid</span>
          <span className="text-2xl font-bold tabular-nums text-[#4369a0]">{repaidRate}%</span>
        </div>
        <Progress value={repaidRate} className="h-2.5 bg-gray-200 [&>div]:bg-[#4369a0]" />
        <p className="mt-2 text-xs text-gray-500">
          {formatCurrency(portfolio.principalRepaid)} repaid of {formatCurrency(portfolio.given)} given
        </p>
      </div>
      <MetricRow label="Total Given" value={formatCurrency(portfolio.given)} />
      <MetricRow label="Principal Repaid" value={formatCurrency(portfolio.principalRepaid)} />
      <MetricRow label="Outstanding" value={formatCurrency(portfolio.outstanding)} />
    </div>
  );
}

function DashboardBody({ summary }: { summary: DashboardSummary }) {
  const { kpis, membershipStatus, memberBalance, loanStatus } = summary;
  const collectionRate = percentOf(kpis.collectedThisMonth, kpis.policyTargetThisMonth);

  return (
    <>
      <div className="mb-6 shrink-0">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Dashboard</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Financial overview and membership health for {formatMonthKey(summary.month)}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xs">
            <TrendingUp className="h-4 w-4 text-[#4369a0]" />
            <div className="text-right">
              <p className="text-xs text-gray-500">This month collection</p>
              <p className="text-sm font-semibold tabular-nums text-gray-900">{collectionRate}% of target</p>
            </div>
          </div>
        </div>
        <div className="h-px bg-gray-200/80" />
      </div>

      <KpiCards kpis={kpis} memberStats={summary.memberStats} />

      <div className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <SectionCard
          title="Monthly Collection"
          description="Policy target, policy received and loan repaid over recent months"
          className="xl:col-span-8"
        >
          <MonthlyCollectionChart data={summary.monthlyCollection} />
        </SectionCard>
        <SectionCard title="Financial Health" description="Current month performance snapshot" className="xl:col-span-4">
          <FinancialHealth summary={summary} />
        </SectionCard>
      </div>

      <div className="mb-6 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
        <SectionCard title="Member Growth" description="Total and new members per month">
          <GrowthChart data={summary.memberGrowth} totalLabel="Total Members" addedLabel="New Members" />
        </SectionCard>
        <SectionCard title="Membership Status" description="Policies by status">
          <DonutChart
            emptyText="No policies yet"
            items={[
              { key: 'active', label: 'Active', value: membershipStatus.active, color: '#059669', boxClass: 'bg-emerald-50 text-emerald-800' },
              { key: 'completed', label: 'Completed', value: membershipStatus.completed, color: '#4369a0', boxClass: 'bg-[#4369a0]/10 text-[#2d4f73]' },
              { key: 'closed', label: 'Closed', value: membershipStatus.closed, color: '#9ca3af', boxClass: 'bg-gray-100 text-gray-700' },
            ]}
          />
        </SectionCard>
        <SectionCard title="Member Balance" description="Paid by PMS amounts with 12% interest, repaid vs still due">
          <DonutChart
            emptyText="No member balances yet"
            formatValue={formatCurrency}
            items={[
              { key: 'repaid', label: 'Repaid', value: memberBalance.repaid, color: '#059669', boxClass: 'bg-emerald-50 text-emerald-800' },
              { key: 'balanceDue', label: 'Balance Due', value: memberBalance.balanceDue, color: '#d97706', boxClass: 'bg-amber-50 text-amber-900' },
              { key: 'interestDue', label: 'Interest Due', value: memberBalance.interestDue, color: '#fbbf24', boxClass: 'bg-amber-50/60 text-amber-800' },
            ]}
          />
        </SectionCard>
      </div>

      <div className="mb-6 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
        {memberBreakdowns(summary.memberStats).map((card) => (
          <SectionCard key={card.title} title={card.title} description={card.description}>
            <div className="space-y-4">
              {card.rows.map(([label, value]) => (
                <MetricRow key={label} label={label} value={memberCount(value)} />
              ))}
            </div>
          </SectionCard>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
        <SectionCard title="Policy Growth" description="Total and new policies per month">
          <GrowthChart data={summary.policyGrowth} totalLabel="Total Policies" addedLabel="New Policies" />
        </SectionCard>
        <SectionCard title="Loan Portfolio" description="Loans given, repaid and outstanding">
          <LoanPortfolio portfolio={summary.loanPortfolio} />
        </SectionCard>
        <SectionCard title="Policy Loan Status" description="Live policies by loan status">
          <DonutChart
            emptyText="No live policies"
            items={[
              { key: 'withLoan', label: 'With Loan', value: loanStatus.withActiveLoan, color: '#4369a0', boxClass: 'bg-[#4369a0]/10 text-[#2d4f73]' },
              { key: 'eligible', label: 'Eligible', value: loanStatus.eligible, color: '#059669', boxClass: 'bg-emerald-50 text-emerald-800' },
              { key: 'notEligible', label: 'Not Eligible', value: loanStatus.notEligible, color: '#9ca3af', boxClass: 'bg-gray-100 text-gray-700' },
            ]}
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <AttentionCard items={summary.attention} month={summary.month} />
        <RecentTransactionsCard items={summary.recentTransactions} />
      </div>
    </>
  );
}

export default function Dashboard() {
  const { isAuthenticated } = useAppContext();
  const { data: summary, isLoading, error } = useDashboardSummaryQuery(isAuthenticated);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50/80">
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
        <div className="p-5">
          {isLoading ? (
            <div className="flex h-[60vh] flex-col items-center justify-center">
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
              <p className="mt-3 text-sm text-gray-500">Loading dashboard...</p>
            </div>
          ) : error || !summary ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Unable to load the dashboard. Please try again.
            </div>
          ) : (
            <DashboardBody summary={summary} />
          )}
        </div>
      </div>
    </div>
  );
}
